import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { SharedServiceService } from './shared-service.service';
import { AllServicesService } from '../../services/all-services.service';

/**
 * Derived Model Inputs view for one blend.
 * Optional dropdown options are only present when the backend returns them.
 */
export interface ModelInputsViewData {
  vmInfo: any[];
  baseOilInfo: any[];
  additives: any[];
  unitOptions?: any[];
  familyOptions?: any[];
  metalOptions?: any[];
}

/**
 * Single source of truth for Model Inputs derivation.
 *
 * Encapsulates the pipeline previously embedded in the Model Inputs modal:
 * build rows from current selections, reconcile stale overrides, decide
 * restore-vs-merge, call the backend, merge preserving saved edits, and
 * persist the result into SharedService.
 *
 * Consumers:
 * - Model Inputs modal (rendering + editing).
 * - Execute Prediction path (force derivation before building the payload).
 */
@Injectable({ providedIn: 'root' })
export class ModelInputsComputationService {
  constructor(
    private sharedService: SharedServiceService,
    private allService: AllServicesService
  ) { }

  /**
   * Derives Model Inputs for a blend from the current selections + saved
   * overrides, persists them into SharedService, and returns the view data.
   * Returns null when there is not enough data to derive (no Base Oil ratio or
   * no valid packages), matching the modal's previous early-return behavior.
   */
  deriveAndPersist(
    blendNumber: number,
    reason: 'modal-load' | 'prediction-build' = 'prediction-build'
  ): Observable<ModelInputsViewData | null> {
    const vmSelectedData = this.sharedService.get_vmSelectedData_value();
    const baseOilSelectedData = this.sharedService.get_baseOilSelectedData_value();
    const packagesData = this.sharedService.get_packagesData_value();

    let vmInfo = this.buildVmInfo(vmSelectedData);
    let baseOilInfo = this.buildBaseOilInfo(baseOilSelectedData);
    let additives = this.buildAdditives(packagesData, blendNumber);

    // Drop stale overrides before deciding restore/merge.
    this.sharedService.reconcileModelInputsForBlend(blendNumber, reason);

    const { storedVm, storedBo, storedDi, hasNewItems } =
      this.detectChangesAndDecide(blendNumber, vmSelectedData, baseOilSelectedData, additives);

    // Release imported authority once the user genuinely changes this blend's selections.
    if (this.sharedService.getModelInputsImported(blendNumber) && hasNewItems) {
      this.sharedService.setModelInputsImported(blendNumber, false);
    }

    // Validation 1: at least one Base Oil with a valid ratio for this blend.
    const ratioField = blendNumber === 1 ? 'ratio1' : 'ratio2';
    const hasBaseOil = baseOilSelectedData && baseOilSelectedData.some((b: any) => {
      const name = b.oil ?? b.name;
      const ratioValue = b[ratioField];
      return name && ratioValue && ratioValue !== '0';
    });
    if (!hasBaseOil) {
      return of(null);
    }

    // Validation 2: at least one valid DI package.
    const hasValidPackages = additives && additives.some((a: any) => a.name && a.name.trim() !== '');
    if (!hasValidPackages) {
      return of(null);
    }

    // If there are NO new items and there is saved data, use only the saved items.
    const hasStoredData = storedVm.length > 0 || storedBo.length > 0 || storedDi.length > 0;
    if (!hasNewItems && hasStoredData) {
      vmInfo = storedVm.map((v: any) => ({
        name: v.name ?? v.VM ?? '',
        SSI30: v.SSI30 ?? '',
        SSI90: v.SSI90 ?? '',
        KV100: v.KV100 ?? '',
        KV40: v.KV40 ?? '',
        NOACK: v.NOACK ?? ''
      }));
      baseOilInfo = storedBo.map((b: any) => ({
        name: b.name ?? b['Base Oil'] ?? '',
        batch: b.batch ?? b.Batch ?? '',
        group: b.group ?? b.Group ?? '',
        saturate: b.saturate ?? b.Saturate ?? '',
        sulfur: b.sulfur ?? b.Sulfur ?? '',
        KV100: b.KV100 ?? '',
        KV40: b.KV40 ?? '',
        VI: b.VI ?? '',
        NOACK: b.NOACK ?? '',
        CCS10: b.CCS10 ?? b['CCS-10'] ?? '',
        CCS15: b.CCS15 ?? b['CCS-15'] ?? '',
        CCS20: b.CCS20 ?? b['CCS-20'] ?? '',
        CCS25: b.CCS25 ?? b['CCS-25'] ?? '',
        CCS30: b.CCS30 ?? b['CCS-30'] ?? '',
        CCS35: b.CCS35 ?? b['CCS-35'] ?? ''
      }));
      additives = additives.map((a: any) => {
        const saved = storedDi.find((s: any) => (s.name || s.Additive) === a.name);
        return saved ? { ...a, family: saved.family || saved.Family || a.family } : a;
      });

      const view: ModelInputsViewData = { vmInfo, baseOilInfo, additives };
      this.persistViewData(blendNumber, view, { persistVm: true }, vmSelectedData);
      return of(view);
    }

    // Call the backend with ALL selected items.
    const inputData = this.prepareInputData(
      blendNumber, vmSelectedData, baseOilSelectedData, additives, vmInfo, baseOilInfo
    );

    const serviceCall = blendNumber === 1
      ? this.allService.get_Model_Inputs(inputData)
      : this.allService.get_Model_InputsPred2(inputData);

    return serviceCall.pipe(
      map((data: any[]) => {
        const view = this.processModelInputsResponseWithMerge(
          data, storedVm, storedBo, storedDi, blendNumber, baseOilSelectedData, additives
        );
        this.persistViewData(blendNumber, view, { persistVm: true }, vmSelectedData);
        return view;
      })
    );
  }

  // #region Selection -> row transforms

  /** Build DI additive rows for a blend from the raw packages data. */
  buildAdditives(packagesData: any[], blendNumber: number): any[] {
    const result: any[] = [];
    if (!packagesData || !Array.isArray(packagesData)) {
      return result;
    }

    const blendIndex = blendNumber - 1; // 0 for pred1, 1 for pred2

    if (packagesData[0] && Array.isArray(packagesData[0])) {
      const pkg1Blend = packagesData[0][blendIndex];
      if (pkg1Blend && pkg1Blend.length > 0) {
        pkg1Blend.forEach((item: any) => {
          result.push({
            name: item.component ?? '',
            dosage: item.treatRate ?? '',
            unit: item.unit ?? '',
            family: item.family ?? '',
            metal: item.blendMetal ?? '',
            typicalMetal: item.typical ?? '',
            packageIndex: 1
          });
        });
      }
    }

    if (packagesData[1] && Array.isArray(packagesData[1])) {
      const pkg2Blend = packagesData[1][blendIndex];
      if (pkg2Blend && pkg2Blend.length > 0) {
        pkg2Blend.forEach((item: any) => {
          result.push({
            name: item.component ?? '',
            dosage: item.treatRate ?? '',
            unit: item.unit ?? '',
            family: item.family ?? '',
            metal: item.blendMetal ?? '',
            typicalMetal: item.typical ?? '',
            packageIndex: 2
          });
        });
      }
    }

    return result;
  }

  /** Build VM info rows from the selected Viscosity Modifiers. */
  buildVmInfo(vmSelectedData: any[]): any[] {
    return (vmSelectedData || []).map((item: any) => ({
      name: item.selectedValue,
      SSI30: item.SSI30 ?? '',
      SSI90: item.SSI90 ?? '',
      KV40: item.KV40 ?? '',
      KV100: item.KV100cSt ?? '',
      NOACK: item.NOACK ?? ''
    }));
  }

  /** Build Base Oil info rows from the selected Base Oils. */
  buildBaseOilInfo(baseOilSelectedData: any[]): any[] {
    return (baseOilSelectedData || []).map((item: any) => ({
      name: item.oil ?? item.name ?? '',
      batch: item.batch ?? '',
      group: item.group ?? '',
      saturate: item.saturate ?? '',
      sulfur: item.sulfur ?? '',
      KV100: item.KV100cSt ?? item.KV100 ?? '',
      KV40: item.KV40 ?? '',
      VI: item.VI ?? '',
      NOACK: item.NOACK ?? '',
      CCS10: item.CCS10 ?? item['CCS-10'] ?? '',
      CCS15: item.CCS15 ?? item['CCS-15'] ?? '',
      CCS20: item.CCS20 ?? item['CCS-20'] ?? '',
      CCS25: item.CCS25 ?? item['CCS-25'] ?? '',
      CCS30: item.CCS30 ?? item['CCS-30'] ?? '',
      CCS35: item.CCS35 ?? item['CCS-35'] ?? ''
    }));
  }

  // #endregion

  /**
   * Compares current selections (main screen) vs saved data.
   * Returns which items to keep from the saved set vs which items are new.
   * PRIORITY: What is on the main screen is the source of truth.
   */
  private detectChangesAndDecide(
    blendNumber: number,
    vmSelectedData: any[],
    baseOilSelectedData: any[],
    additives: any[]
  ): { storedVm: any[]; storedBo: any[]; storedDi: any[]; hasNewItems: boolean } {
    const savedVm = blendNumber === 1
      ? this.sharedService.get_mod_inp_vm_value()
      : this.sharedService.get_mod_inp_vm2_value();
    const savedBo = blendNumber === 1
      ? this.sharedService.get_mod_inp_bo_value()
      : this.sharedService.get_mod_inp_bo2_value();
    const savedDi = blendNumber === 1
      ? this.sharedService.get_mod_inp_di_value()
      : this.sharedService.get_mod_inp_di2_value();

    const treatField = blendNumber === 1 ? 'treat1' : 'treat2';
    const ratioField = blendNumber === 1 ? 'ratio1' : 'ratio2';

    const currentVmNames = new Set(
      (vmSelectedData || [])
        .filter((v: any) => v[treatField] && v[treatField] !== '0')
        .map((v: any) => v.selectedValue || v.name || '')
    );

    const currentBoKeys = new Set(
      (baseOilSelectedData || [])
        .filter((b: any) => b[ratioField] && b[ratioField] !== '0')
        .map((b: any) => `${b.oil ?? b.name}_${b.batch ?? ''}`)
    );

    const currentDiNames = new Set(
      (additives || [])
        .filter((a: any) => a.name && a.name.trim() !== '')
        .map((a: any) => a.name)
    );

    const storedVm = (savedVm || []).filter((v: any) =>
      currentVmNames.has(v.name || v.VM || '')
    );
    const storedBo = (savedBo || []).filter((b: any) => {
      const key = `${b.name ?? b['Base Oil']}_${b.batch ?? b.Batch ?? ''}`;
      return currentBoKeys.has(key);
    });
    const storedDi = (savedDi || []).filter((d: any) =>
      currentDiNames.has(d.name || d.Additive || '')
    );

    const savedVmNames = new Set((savedVm || []).map((v: any) => v.name || v.VM || ''));
    const savedBoKeys = new Set((savedBo || []).map((b: any) =>
      `${b.name ?? b['Base Oil']}_${b.batch ?? b.Batch ?? ''}`
    ));
    const savedDiNames = new Set((savedDi || []).map((d: any) => d.name || d.Additive || ''));

    let hasNewItems = false;

    for (const name of currentVmNames) {
      if (!savedVmNames.has(name)) { hasNewItems = true; break; }
    }
    if (!hasNewItems) {
      for (const key of currentBoKeys) {
        if (!savedBoKeys.has(key)) { hasNewItems = true; break; }
      }
    }
    if (!hasNewItems) {
      for (const name of currentDiNames) {
        if (!savedDiNames.has(name)) { hasNewItems = true; break; }
      }
    }

    return { storedVm, storedBo, storedDi, hasNewItems };
  }

  // Prepare input data (equivalent to model_inputs_pred1_input)
  private prepareInputData(
    blendNumber: number,
    vmSelectedData: any[],
    baseOilSelectedData: any[],
    additives: any[],
    vmInfo: any[],
    baseOilInfo: any[]
  ): any {
    const expandVmOverridesToSelection = (vmOverrides: any[], selectedVmNames: string[]): any[] => {
      if (!vmOverrides || vmOverrides.length === 0) return vmOverrides || [];
      if (!selectedVmNames || selectedVmNames.length === 0) return vmOverrides;

      const firstRow = vmOverrides[0] ?? {};
      const columns = Object.keys(firstRow);
      const columnsWithVm = columns.includes('VM') ? columns : ['VM', ...columns];

      const normalizeName = (row: any): string => (row?.VM ?? row?.name ?? '').toString();
      const overrideByName = new Map(vmOverrides.map(r => [normalizeName(r), r]));

      return selectedVmNames
        .filter((name: any) => !!name && name !== '0')
        .map((vmName: string) => {
          const existing = overrideByName.get(vmName);
          if (existing) {
            return { ...existing, VM: existing.VM ?? vmName };
          }
          const placeholder: any = {};
          for (const col of columnsWithVm) {
            placeholder[col] = col === 'VM' ? vmName : null;
          }
          return placeholder;
        });
    };

    const vmArray: any[] = [];
    const ppdArray: any[] = [];
    const boArray: any[] = [];

    vmInfo.forEach(vm => {
      if (vm.name) {
        const treatField = blendNumber === 1 ? 'treat1' : 'treat2';
        const vmItem = vmSelectedData.find(v => v.selectedValue === vm.name);
        const treatValue = vmItem ? vmItem[treatField] : null;
        if (treatValue && treatValue !== '0') {
          vmArray.push([vm.name, treatValue]);
        }
      }
    });

    if (vmArray.length === 0) {
      vmArray.push(['0', NaN]);
      vmArray.push(['0', NaN]);
    }

    const ratioField = blendNumber === 1 ? 'ratio1' : 'ratio2';
    (baseOilSelectedData || []).forEach((b: any) => {
      const name = b.oil ?? b.name;
      const batch = b.batch ?? '';
      const ratioValue = b[ratioField];
      if (name && ratioValue && ratioValue !== '0') {
        boArray.push([name, batch, ratioValue]);
      }
    });

    if (ppdArray.length === 0) {
      ppdArray.push(['0', NaN]);
      ppdArray.push(['0', NaN]);
    }

    const diPackageNums: any[] = [];
    const diAdditives: any[] = [];
    const diDosages: any[] = [];
    const dilPackageNums: any[] = [];
    const dilTreats: any[] = [];

    if (additives && additives.length > 0) {
      additives.forEach((a: any) => {
        if (a.name && a.name.trim() !== '') {
          diPackageNums.push(a.packageIndex ?? 0);
          diAdditives.push(a.name);
          diDosages.push(a.dosage ?? 0);
          dilPackageNums.push(a.packageIndex ?? 0);
          dilTreats.push(a.dosage ?? 0);
        }
      });
    }

    if (diPackageNums.length === 0) {
      diPackageNums.push(0);
      diAdditives.push('');
      diDosages.push(0);
      dilPackageNums.push(0);
      dilTreats.push(0);
    }

    const diCols = ['Additive', 'Dosage', 'Unit', 'Family', 'Metal', 'Typ. Metal %'].map(h => ({ field: h, header: h }));
    const boCols = ['Base Oil', 'Batch', 'Group', 'Saturate', 'Sulfur', 'KV100', 'KV40', 'VI', 'NOACK', 'CCS-10', 'CCS-15', 'CCS-20', 'CCS-25', 'CCS-30', 'CCS-35']
      .map(h => ({ field: h, header: h }));

    const mapAdditives = (rows: any[]) => (rows || [])
      .map(a => ({
        'Additive': a.name ?? a['Additive'] ?? '',
        'Dosage': a.dosage ?? a['Dosage'] ?? '',
        'Unit': a.unit ?? a['Unit'] ?? '',
        'Family': a.family ?? a['Family'] ?? '',
        'Metal': a.metal ?? a['Metal'] ?? '',
        'Typ. Metal %': a.typicalMetal ?? a['Typ. Metal %'] ?? ''
      }))
      .filter(r => r['Additive'] && r['Additive'].toString().trim() !== '');

    const mapBO = (rows: any[]) => (rows || [])
      .map(b => ({
        'Base Oil': b.name ?? b['Base Oil'] ?? b.oil ?? '',
        'Batch': b.batch ?? b['Batch'] ?? '',
        'Group': (b.group ?? b['Group'] ?? '') !== '' ? String(b.group ?? b['Group']) : '',
        'Saturate': b.saturate ?? b['Saturate'] ?? '',
        'Sulfur': b.sulfur ?? b['Sulfur'] ?? '',
        'KV100': b.KV100 ?? b['KV100'] ?? b.KV100cSt ?? '',
        'KV40': b.KV40 ?? b['KV40'] ?? '',
        'VI': b.VI ?? b['VI'] ?? '',
        'NOACK': b.NOACK ?? b['NOACK'] ?? '',
        'CCS-10': b.CCS10 ?? b['CCS-10'] ?? '',
        'CCS-15': b.CCS15 ?? b['CCS-15'] ?? '',
        'CCS-20': b.CCS20 ?? b['CCS-20'] ?? '',
        'CCS-25': b.CCS25 ?? b['CCS-25'] ?? '',
        'CCS-30': b.CCS30 ?? b['CCS-30'] ?? '',
        'CCS-35': b.CCS35 ?? b['CCS-35'] ?? ''
      }))
      .filter(r => r['Base Oil']);

    const mi_di_rows_raw = this.getMergedDIData(blendNumber, additives, mapAdditives);

    const savedVmOverridesRaw = blendNumber === 1
      ? this.sharedService.get_mod_inp_vm_value()
      : this.sharedService.get_mod_inp_vm2_value();

    let mi_vm_rows = (savedVmOverridesRaw || [])
      .filter((row: any) => {
        const vmName = row?.VM ?? row?.name ?? '';
        return !!vmName && this.hasValidTreat(vmName, vmSelectedData, blendNumber);
      });

    if (mi_vm_rows.length > 0) {
      const treatField = blendNumber === 1 ? 'treat1' : 'treat2';
      const selectedVmNames = (vmSelectedData || [])
        .filter((vm: any) => {
          const t = vm?.[treatField];
          return vm?.selectedValue && t && t !== '0';
        })
        .map((vm: any) => vm.selectedValue);
      mi_vm_rows = expandVmOverridesToSelection(mi_vm_rows, selectedVmNames);
    }

    const hasValidFamily = mi_di_rows_raw.some((row: any) => row['Family'] && row['Family'].trim() !== '');
    const mi_di_rows = hasValidFamily ? mi_di_rows_raw : [];

    const hasCompleteProperties = baseOilInfo.length > 0 &&
      (baseOilInfo[0].group || baseOilInfo[0].saturate);
    const mi_bo_rows = hasCompleteProperties ? mapBO(baseOilInfo) : [];

    const mi_di_cols = mi_di_rows.length ? diCols : [];
    const mi_vm_cols = mi_vm_rows.length ? Object.keys(mi_vm_rows[0]).map(header => ({ header })) : [];
    const mi_bo_cols = mi_bo_rows.length ? boCols : [];

    const mod_inp_vm = blendNumber === 1 ? mi_vm_rows : [];
    const mod_inp_col_vm = blendNumber === 1 ? mi_vm_cols : [];
    const mod_inp_vm2 = blendNumber === 2 ? mi_vm_rows : [];
    const mod_inp_col_vm2 = blendNumber === 2 ? mi_vm_cols : [];

    const inputData = {
      vm_data: vmArray,
      ppd_data: ppdArray,
      bo_data: boArray,
      di_data: [],
      dil: [],
      di_inp_package_Num: diPackageNums,
      di_inp_additive: diAdditives,
      di_inp_dossage: diDosages,
      dil_package_Num: dilPackageNums,
      dil_Treat_wt_package: dilTreats,
      mod_inp_di: mi_di_rows,
      mod_inp_vm: mod_inp_vm,
      mod_inp_ppd: [],
      mod_inp_bo: mi_bo_rows,
      mod_inp_col_di: mi_di_cols,
      mod_inp_col_vm: mod_inp_col_vm,
      mod_inp_col_bo: mi_bo_cols,
      mod_inp_di2: [],
      mod_inp_vm2: mod_inp_vm2,
      mod_inp_ppd2: [],
      mod_inp_bo2: [],
      mod_inp_col_di2: [],
      mod_inp_col_vm2: mod_inp_col_vm2,
      mod_inp_col_bo2: [],
      pred_num: blendNumber
    };

    return inputData;
  }

  /**
   * Processes backend response by merging with saved data.
   * Stored items keep their edited values; new items use backend values.
   */
  private processModelInputsResponseWithMerge(
    data: any[],
    storedVm: any[],
    storedBo: any[],
    storedDi: any[],
    blendNumber: number,
    baseOilSelectedData: any[],
    additives: any[]
  ): ModelInputsViewData {
    const view: ModelInputsViewData = { vmInfo: [], baseOilInfo: [], additives };

    if (!data || !Array.isArray(data)) {
      return view;
    }

    const storedVmMap = new Map(storedVm.map(v => [v.name || v.VM, v]));
    const storedBoMap = new Map(storedBo.map(b => [`${b.name ?? b['Base Oil']}_${b.batch ?? b.Batch}`, b]));
    const storedDiMap = new Map(storedDi.map(d => [d.name || d.Additive, d]));

    const viMap = new Map(
      (baseOilSelectedData || []).map((b: any) => [
        `${b.oil ?? b.name}_${b.batch ?? ''}`,
        b.VI ?? b.vi ?? ''
      ])
    );

    if (data.length >= 4) {
      if (data[0] && data[0].length > 0) {
        view.vmInfo = data[0].map((item: any) => {
          const vmName = item['VM'] ?? '';
          const stored = storedVmMap.get(vmName);
          if (stored) {
            return {
              name: stored.name ?? stored.VM ?? vmName,
              SSI30: stored.SSI30 ?? '',
              SSI90: stored.SSI90 ?? '',
              KV100: stored.KV100 ?? '',
              KV40: stored.KV40 ?? '',
              NOACK: stored.NOACK ?? ''
            };
          }
          return {
            name: vmName,
            SSI30: item['SSI30'] ?? '',
            SSI90: item['SSI90'] ?? '',
            KV100: item['KV100'] ?? '',
            KV40: item['KV40'] ?? '',
            NOACK: item['NOACK'] ?? ''
          };
        });

        const vm_columns = Object.keys(data[0][0]);
        if (blendNumber === 1) {
          this.sharedService.set_mod_inp_col_vm(vm_columns);
        } else {
          this.sharedService.set_mod_inp_col_vm2(vm_columns);
        }
      }

      if (data[1] && data[1].length > 0) {
        view.baseOilInfo = data[1].map((item: any) => {
          const name = item['Base Oil'] ?? '';
          const batch = item['Batch'] ?? '';
          const key = `${name}_${batch}`;
          const stored = storedBoMap.get(key);
          if (stored) {
            return {
              name: stored.name ?? stored['Base Oil'] ?? name,
              batch: stored.batch ?? stored.Batch ?? batch,
              group: stored.group ?? stored.Group ?? item['Group'] ?? '',
              saturate: stored.saturate ?? stored.Saturate ?? item['Saturate'] ?? '',
              sulfur: stored.sulfur ?? stored.Sulfur ?? item['Sulfur'] ?? '',
              KV100: stored.KV100 ?? item['KV100'] ?? '',
              KV40: stored.KV40 ?? item['KV40'] ?? '',
              VI: stored.VI ?? item['VI'] ?? '',
              NOACK: stored.NOACK ?? item['NOACK'] ?? '',
              CCS10: stored.CCS10 ?? stored['CCS-10'] ?? item['CCS-10'] ?? '',
              CCS15: stored.CCS15 ?? stored['CCS-15'] ?? item['CCS-15'] ?? '',
              CCS20: stored.CCS20 ?? stored['CCS-20'] ?? item['CCS-20'] ?? '',
              CCS25: stored.CCS25 ?? stored['CCS-25'] ?? item['CCS-25'] ?? '',
              CCS30: stored.CCS30 ?? stored['CCS-30'] ?? item['CCS-30'] ?? '',
              CCS35: stored.CCS35 ?? stored['CCS-35'] ?? item['CCS-35'] ?? ''
            };
          }
          return {
            name,
            batch,
            group: item['Group'] ?? '',
            saturate: item['Saturate'] ?? '',
            sulfur: item['Sulfur'] ?? '',
            KV100: item['KV100'] ?? '',
            KV40: item['KV40'] ?? '',
            VI: viMap.get(key) ?? '',
            NOACK: item['NOACK'] ?? '',
            CCS10: item['CCS-10'] ?? '',
            CCS15: item['CCS-15'] ?? '',
            CCS20: item['CCS-20'] ?? '',
            CCS25: item['CCS-25'] ?? '',
            CCS30: item['CCS-30'] ?? '',
            CCS35: item['CCS-35'] ?? ''
          };
        });

        const bo_columns = Object.keys(data[1][0]);
        if (blendNumber === 1) {
          this.sharedService.set_mod_inp_col_bo(bo_columns);
        } else {
          this.sharedService.set_mod_inp_col_bo2(bo_columns);
        }
      }

      if (data[2] && data[2].length > 0) {
        const familyMap = new Map(data[2].map((item: any) => [item['Additive'], item['Family']]));
        view.additives = additives.map((a: any) => {
          const stored = storedDiMap.get(a.name);
          if (stored && (stored.family || stored.Family)) {
            return { ...a, family: stored.family || stored.Family };
          }
          const family = familyMap.get(a.name) ?? a.family ?? '';
          return { ...a, family };
        });

        const di_columns = Object.keys(data[2][0]);
        if (blendNumber === 1) {
          this.sharedService.set_mod_inp_col_di(di_columns);
        } else {
          this.sharedService.set_mod_inp_col_di2(di_columns);
        }
      }

      if (data[3] && data[3]['DI']) {
        view.unitOptions = data[3]['DI']['Unit'] || [];
        view.familyOptions = data[3]['DI']['Family'] || [];
        view.metalOptions = data[3]['DI']['Metal'] || [];
      }

    } else if (data.length === 3) {
      if (blendNumber === 1) {
        this.sharedService.set_mod_inp_col_vm([]);
      } else {
        this.sharedService.set_mod_inp_col_vm2([]);
      }

      if (data[0] && data[0].length > 0) {
        view.baseOilInfo = data[0].map((item: any) => {
          const name = item['Base Oil'] ?? '';
          const batch = item['Batch'] ?? '';
          const key = `${name}_${batch}`;
          const stored = storedBoMap.get(key);
          if (stored) {
            return {
              name: stored.name ?? stored['Base Oil'] ?? name,
              batch: stored.batch ?? stored.Batch ?? batch,
              group: stored.group ?? stored.Group ?? item['Group'] ?? '',
              saturate: stored.saturate ?? stored.Saturate ?? item['Saturate'] ?? '',
              sulfur: stored.sulfur ?? stored.Sulfur ?? item['Sulfur'] ?? '',
              KV100: stored.KV100 ?? item['KV100'] ?? '',
              KV40: stored.KV40 ?? item['KV40'] ?? '',
              VI: stored.VI ?? item['VI'] ?? '',
              NOACK: stored.NOACK ?? item['NOACK'] ?? '',
              CCS10: stored.CCS10 ?? stored['CCS-10'] ?? item['CCS-10'] ?? '',
              CCS15: stored.CCS15 ?? stored['CCS-15'] ?? item['CCS-15'] ?? '',
              CCS20: stored.CCS20 ?? stored['CCS-20'] ?? item['CCS-20'] ?? '',
              CCS25: stored.CCS25 ?? stored['CCS-25'] ?? item['CCS-25'] ?? '',
              CCS30: stored.CCS30 ?? stored['CCS-30'] ?? item['CCS-30'] ?? '',
              CCS35: stored.CCS35 ?? stored['CCS-35'] ?? item['CCS-35'] ?? ''
            };
          }
          return {
            name,
            batch,
            group: item['Group'] ?? '',
            saturate: item['Saturate'] ?? '',
            sulfur: item['Sulfur'] ?? '',
            KV100: item['KV100'] ?? '',
            KV40: item['KV40'] ?? '',
            VI: viMap.get(key) ?? '',
            NOACK: item['NOACK'] ?? '',
            CCS10: item['CCS-10'] ?? '',
            CCS15: item['CCS-15'] ?? '',
            CCS20: item['CCS-20'] ?? '',
            CCS25: item['CCS-25'] ?? '',
            CCS30: item['CCS-30'] ?? '',
            CCS35: item['CCS-35'] ?? ''
          };
        });

        const bo_columns = Object.keys(data[0][0]);
        if (blendNumber === 1) {
          this.sharedService.set_mod_inp_col_bo(bo_columns);
        } else {
          this.sharedService.set_mod_inp_col_bo2(bo_columns);
        }
      }

      if (data[1] && data[1].length > 0) {
        const familyMap = new Map(data[1].map((item: any) => [item['Additive'], item['Family']]));
        view.additives = additives.map((a: any) => {
          const stored = storedDiMap.get(a.name);
          if (stored && (stored.family || stored.Family)) {
            return { ...a, family: stored.family || stored.Family };
          }
          return { ...a, family: familyMap.get(a.name) ?? a.family ?? '' };
        });

        const di_columns = Object.keys(data[1][0]);
        if (blendNumber === 1) {
          this.sharedService.set_mod_inp_col_di(di_columns);
        } else {
          this.sharedService.set_mod_inp_col_di2(di_columns);
        }
      }
    }

    return view;
  }

  /**
   * Persists edited/derived view rows into SharedService in backend format.
   * VM overrides are only persisted when persistVm is true (edits require Save).
   */
  persistViewData(
    blendNumber: number,
    view: ModelInputsViewData,
    options: { persistVm?: boolean } | undefined,
    vmSelectedData: any[]
  ): void {
    const persistVm = options?.persistVm === true;
    const toNullIfEmpty = (val: any): any => {
      if (val === '' || val === null || val === undefined || val === 'NaN') return null;
      return val;
    };

    const vmForBackend = (view.vmInfo || []).map((v: any) => ({
      'VM': v.name ?? v.VM ?? '',
      'SSI30': toNullIfEmpty(v.SSI30),
      'SSI90': toNullIfEmpty(v.SSI90),
      'KV100': toNullIfEmpty(v.KV100),
      'KV40': toNullIfEmpty(v.KV40),
      'NOACK': toNullIfEmpty(v.NOACK)
    }))
      .filter((row: any) => row['VM'] && this.hasValidTreat(row['VM'], vmSelectedData, blendNumber));

    const boForBackend = (view.baseOilInfo || []).map((b: any) => ({
      'Base Oil': b.name ?? b['Base Oil'] ?? '',
      'Batch': b.batch ?? b.Batch ?? '',
      'Group': b.group ?? b.Group ?? '',
      'Saturate': toNullIfEmpty(b.saturate ?? b.Saturate),
      'Sulfur': toNullIfEmpty(b.sulfur ?? b.Sulfur),
      'KV100': toNullIfEmpty(b.KV100),
      'KV40': toNullIfEmpty(b.KV40),
      'VI': toNullIfEmpty(b.VI),
      'NOACK': toNullIfEmpty(b.NOACK),
      'CCS-10': toNullIfEmpty(b.CCS10 ?? b['CCS-10']),
      'CCS-15': toNullIfEmpty(b.CCS15 ?? b['CCS-15']),
      'CCS-20': toNullIfEmpty(b.CCS20 ?? b['CCS-20']),
      'CCS-25': toNullIfEmpty(b.CCS25 ?? b['CCS-25']),
      'CCS-30': toNullIfEmpty(b.CCS30 ?? b['CCS-30']),
      'CCS-35': toNullIfEmpty(b.CCS35 ?? b['CCS-35'])
    }));

    const diForBackend = (view.additives || []).map((a: any) => ({
      'Additive': a.name ?? a.Additive ?? '',
      'Dosage': toNullIfEmpty(a.dosage ?? a.Dosage),
      'Unit': a.unit ?? a.Unit ?? '',
      'Family': a.family ?? a.Family ?? '',
      'Metal': a.metal ?? a.Metal ?? '',
      'Typ. Metal %': toNullIfEmpty(a.typicalMetal ?? a['Typ. Metal %'])
    }));

    if (blendNumber === 1) {
      if (persistVm) {
        this.sharedService.set_mod_inp_vm(vmForBackend);
      }
      this.sharedService.set_mod_inp_bo(boForBackend);
      this.sharedService.set_mod_inp_di(diForBackend);
    } else {
      if (persistVm) {
        this.sharedService.set_mod_inp_vm2(vmForBackend);
      }
      this.sharedService.set_mod_inp_bo2(boForBackend);
      this.sharedService.set_mod_inp_di2(diForBackend);
    }
  }

  /**
   * Merged DI data: combines additives with saved SharedService DI data
   * (which carries Family from a previous get_Model_Inputs response).
   */
  private getMergedDIData(
    blendNumber: number,
    additives: any[],
    mapAdditives: (rows: any[]) => any[]
  ): any[] {
    const savedDI = blendNumber === 1
      ? this.sharedService.get_mod_inp_di_value()
      : this.sharedService.get_mod_inp_di2_value();

    if (!savedDI || savedDI.length === 0) {
      return mapAdditives(additives);
    }

    const mergedAdditives = additives.map((add: any) => {
      const savedItem = savedDI.find((s: any) =>
        (s['Additive'] || s.name) === (add.name || add['Additive'])
      );
      if (savedItem && (savedItem['Family'] || savedItem.family)) {
        return {
          ...add,
          family: savedItem['Family'] || savedItem.family || add.family || ''
        };
      }
      return add;
    });

    return mapAdditives(mergedAdditives);
  }

  /** Whether a VM has a treat value for the current blend. */
  private hasValidTreat(vmName: string, vmSelectedData: any[], blendNumber: number): boolean {
    const treatField = blendNumber === 1 ? 'treat1' : 'treat2';
    const vmItem = (vmSelectedData || []).find(vm => vm.selectedValue === vmName);
    const treatValue = vmItem ? vmItem[treatField] : null;
    return treatValue && treatValue !== '0' && treatValue !== null && treatValue !== undefined;
  }
}
